import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import type {
  InventoryReplenishmentItem,
  InventoryReplenishmentLine,
} from '@/utils/approval/inventoryReplenishment';

/** 작성 화면이 이 칸에 삭제 버튼을 그림 */
export const ACTION_CELL_CLASS = 'irp-action';
export const CARD_CLASS = 'irp-card';

const COL_WIDTHS = [
  '8%',
  '13%',
  '10%',
  '19%',
  '5%',
  '6%',
  '5%',
  '5%',
  '7%',
  '10%',
  '7%',
];

const text = (v: unknown) =>
  v === null || v === undefined ? '' : String(v).trim();

/** 레거시 number_format */
const formatNumber = (v: unknown) => {
  const raw = text(v);
  const n = Number(raw.replace(/,/g, ''));
  return raw && Number.isFinite(n)
    ? Math.round(n).toLocaleString('en-US')
    : raw;
};

/** 업체 칸은 레거시처럼 첫 줄 값으로 전체 줄을 합침 */
const LineRow = ({
  line,
  vendorRowSpan,
}: {
  line: InventoryReplenishmentLine;
  vendorRowSpan: number;
}) => (
  <tr>
    {vendorRowSpan > 0 && (
      <>
        <td
          className="text-center"
          rowSpan={vendorRowSpan > 1 ? vendorRowSpan : undefined}
        >
          {text(line.vdCd)}
        </td>
        <td
          className="text-center"
          rowSpan={vendorRowSpan > 1 ? vendorRowSpan : undefined}
        >
          {text(line.vdNm)}
        </td>
      </>
    )}
    <td className="text-center">{text(line.itemCd)}</td>
    <td>{text(line.itemNm)}</td>
    <td className="text-center">{text(line.colorCd)}</td>
    <td className="text-center">{text(line.sizeCd)}</td>
    <td className="text-center">{text(line.unitCd)}</td>
    <td className="text-center">{formatNumber(line.itemQty)}</td>
    <td className="text-center">{formatNumber(line.itemPrice)}</td>
    <td className="text-center">{formatNumber(line.itemAmt)}</td>
    <td className="text-center">{text(line.cur)}</td>
  </tr>
);

/** 레거시 appr_sub_view_forms_supplies_mstr — 건마다 카드 하나 */
const Card = ({
  item,
  t,
}: {
  item: InventoryReplenishmentItem;
  t: TFunction;
}) => (
  <div
    className={CARD_CLASS}
    data-item-id={item.aprvId}
    data-doc-tit={item.docTit}
  >
    <table>
      <colgroup>
        {COL_WIDTHS.map((width, i) => (
          <col key={i} width={width} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th className="card-title" colSpan={9}>
            ({item.docTit})
          </th>
          <th className={ACTION_CELL_CLASS} colSpan={2} />
        </tr>
        <tr>
          <th colSpan={2}>{t('업체')}</th>
          <th colSpan={9}>{t('내역')}</th>
        </tr>
        <tr>
          <th>{t('코드')}</th>
          <th>{t('업체명')}</th>
          <th>{t('스타일코드')}</th>
          <th>{t('스타일명')}</th>
          <th>{t('컬러')}</th>
          <th>{t('사이즈')}</th>
          <th>{t('단위')}</th>
          <th>{t('수량')}</th>
          <th>{t('단가')}</th>
          <th>{t('금액')}</th>
          <th>{t('통화')}</th>
        </tr>
      </thead>
      <tbody>
        {item.lines.map((line, i) => (
          <LineRow
            key={i}
            line={line}
            vendorRowSpan={i === 0 ? item.lines.length : 0}
          />
        ))}
      </tbody>
    </table>
  </div>
);

/** 저장 본문 — 고른 순서대로 카드 */
export const buildInventoryReplenishmentHtml = (
  items: InventoryReplenishmentItem[],
  t: TFunction,
): string =>
  items.length
    ? renderToStaticMarkup(
        <>
          {items.map((item) => (
            <Card key={item.aprvId} item={item} t={t} />
          ))}
        </>,
      )
    : '';
